import React, { useState, useEffect } from 'react';
import Modal from '../../general/Modal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faCheck, faX, faExclamationTriangle, faInfoCircle, faChevronDown, faChevronUp, faSearch } from '@fortawesome/pro-solid-svg-icons';
import useAI from '../../../hooks/useAI';
import useTranslate from '../../../hooks/useTranslate';

const ProofreaderModal = ({ closeFunction, cards, allRows, columns, deckTitle, onApplyChanges }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [keptTranslations, setKeptTranslations] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [processedCards, setProcessedCards] = useState(0);
  const [totalCards, setTotalCards] = useState(cards.length);

  // Initialize selected cards when component mounts or cards change
  useEffect(() => {
    if (cards.length > 0) {
      const allCardIndices = new Set(cards.map((_, index) => index));
      setSelectedCards(allCardIndices);
      setSelectAllCards(true);
      setTotalCards(cards.length);
    }
  }, [cards]);
  const [currentCardLanguages, setCurrentCardLanguages] = useState({ current: 0, total: 0 }); // Track language batch progress
  const [acceptedSuggestions, setAcceptedSuggestions] = useState(new Set());
  const [rejectedSuggestions, setRejectedSuggestions] = useState(new Set());
  const [severityFilter, setSeverityFilter] = useState('all'); // 'all', 'error', 'warning', 'suggestion'
  const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'accepted', 'rejected', 'pending'
  const [updateTypeFilter, setUpdateTypeFilter] = useState('all'); // 'all', 'spelling', 'grammar', 'word-choice', 'formatting', 'other'
  const [sortBy, setSortBy] = useState('severity'); // 'severity', 'language', 'card', 'none'
  const [showKept, setShowKept] = useState(true); // Toggle to show/hide kept translations
  const [expandedSuggestions, setExpandedSuggestions] = useState(new Set()); // Track which suggestions are expanded
  const [deckContext, setDeckContext] = useState(""); // User-provided context about the deck's purpose and words
  const [googleTranslateResults, setGoogleTranslateResults] = useState({}); // Store Google Translate results by suggestion ID
  const [googleTranslateLoading, setGoogleTranslateLoading] = useState(new Set()); // Track which suggestions are loading Google Translate
  const [selectedCards, setSelectedCards] = useState(new Set()); // Track which cards are selected for proofreading
  const [selectAllCards, setSelectAllCards] = useState(true); // Track if "select all" is checked
  const [suggestingPrompt, setSuggestingPrompt] = useState(false); // Track if AI is suggesting a prompt
  
  // Helper function to check if there's a meaningful change (ignoring leading/trailing whitespace)
  const hasMeaningfulChange = (original, suggested) => {
    if (!original || !suggested) return false;
    const normalizedOriginal = original.trim();
    const normalizedSuggested = suggested.trim();
    return normalizedOriginal !== normalizedSuggested;
  };
  
  const { proofreadMultilingualCard, suggestDeckContextPrompt } = useAI();
  const { translateText } = useTranslate();


  const handleSelectAllCards = (checked) => {
    setSelectAllCards(checked);
    if (checked) {
      const allCardIndices = new Set(cards.map((_, index) => index));
      setSelectedCards(allCardIndices);
    } else {
      setSelectedCards(new Set());
    }
  };

  const handleToggleCard = (cardIndex) => {
    const newSelected = new Set(selectedCards);
    if (newSelected.has(cardIndex)) {
      newSelected.delete(cardIndex);
      if (newSelected.size === 0) {
        setSelectAllCards(false);
      }
    } else {
      newSelected.add(cardIndex);
      if (newSelected.size === cards.length) {
        setSelectAllCards(true);
      }
    }
    setSelectedCards(newSelected);
  };

  const handleSuggestPrompt = async () => {
    setSuggestingPrompt(true);
    try {
      // Prepare sample cards (first 5 cards with their translations)
      const validColumns = columns.filter(col => col !== null);
      const sampleCards = cards.slice(0, 5).map(card => {
        const cardData = {};
        validColumns.forEach(lang => {
          const value = card[lang];
          if (value && value !== null && value !== "") {
            cardData[lang] = value;
          }
        });
        return cardData;
      });

      const result = await suggestDeckContextPrompt({
        deckTitle: deckTitle || "",
        sampleCards: sampleCards
      });

      if (result && result.suggestedPrompt) {
        setDeckContext(result.suggestedPrompt);
      }
    } catch (error) {
      console.error("Error suggesting prompt:", error);
    } finally {
      setSuggestingPrompt(false);
    }
  };

  const startProofreading = async () => {
    // Filter cards based on selection
    const cardsToProcess = cards.filter((_, index) => selectedCards.has(index));
    
    if (cardsToProcess.length === 0) {
      // Show error notification - will be handled by parent component
      return;
    }

    setProcessing(true);
    setProcessedCards(0);
    setSuggestions([]);
    setKeptTranslations([]);
    setAcceptedSuggestions(new Set());
    setRejectedSuggestions(new Set());
    setCurrentCardLanguages({ current: 0, total: 0 });

    const validColumns = columns.filter(col => col !== null);
    const LANGUAGE_BATCH_SIZE = 8; // Process 8 languages at a time to ensure thorough checking
    const allSuggestions = [];
    const allKept = [];

    // Process selected cards one at a time
    // Get sorted indices to maintain order
    const sortedSelectedIndices = Array.from(selectedCards).sort((a, b) => a - b);
    
    for (let i = 0; i < cardsToProcess.length; i++) {
      const card = cardsToProcess[i];
      const cardOriginalIndex = sortedSelectedIndices[i];
      
      // Format card for AI (only include languages that have values)
      const cardForAI = {};
      const cardLanguages = [];
      validColumns.forEach(lang => {
        const value = card[lang];
        if (value && value !== null && value !== "") {
          cardForAI[lang] = value;
          cardLanguages.push(lang);
        }
      });

      // Skip cards with no translations
      if (cardLanguages.length === 0) {
        setProcessedCards(i + 1);
        continue;
      }

      // Find original row index in allRows array
      const originalRowIndex = allRows ? allRows.findIndex(row => {
        if (card.card_id && row.card_id) {
          return row.card_id === card.card_id;
        }
        // Match by comparing translations
        const validColumns = columns.filter(col => col !== null);
        return validColumns.every(lang => {
          const cardVal = card[lang];
          const rowVal = row[lang];
          return (cardVal === rowVal) || (!cardVal && !rowVal);
        });
      }) : (cardOriginalIndex >= 0 ? cardOriginalIndex : i);

      // Process languages in batches for this card
      const totalLanguageBatches = Math.ceil(cardLanguages.length / LANGUAGE_BATCH_SIZE);
      for (let langBatchStart = 0; langBatchStart < cardLanguages.length; langBatchStart += LANGUAGE_BATCH_SIZE) {
        const langBatch = cardLanguages.slice(langBatchStart, langBatchStart + LANGUAGE_BATCH_SIZE);
        const currentBatch = Math.floor(langBatchStart / LANGUAGE_BATCH_SIZE) + 1;
        
        // Update language batch progress
        setCurrentCardLanguages({ 
          current: langBatchStart + langBatch.length, 
          total: cardLanguages.length 
        });
        
        // Create a subset of the card with only languages in this batch
        const cardBatchForAI = {};
        langBatch.forEach(lang => {
          cardBatchForAI[lang] = cardForAI[lang];
        });

        try {
          // Get all translations from the full card for context
          const allCardTranslations = {};
          validColumns.forEach(lang => {
            const value = card[lang];
            if (value && value !== null && value !== "") {
              allCardTranslations[lang] = value;
            }
          });

          const result = await proofreadMultilingualCard({ 
            card: cardBatchForAI, 
            languages: langBatch,
            deckTitle: deckTitle,
            allCardTranslations: allCardTranslations, // Full card context
            deckContext: deckContext || "" // User-provided context about deck purpose
          });

          // Handle both old format (array) and new format (object)
          let batchSuggestions = [];
          let batchKept = [];
          
          if (Array.isArray(result)) {
            // Old format - just suggestions array
            batchSuggestions = result;
          } else if (result && typeof result === 'object') {
            // New format - object with suggestions and kept
            batchSuggestions = Array.isArray(result.suggestions) ? result.suggestions : [];
            batchKept = Array.isArray(result.kept) ? result.kept : [];
          }

          const enrichedSuggestions = batchSuggestions.map(suggestion => ({
            ...suggestion,
            id: `${originalRowIndex}_${suggestion.language}`,
            cardIndex: originalRowIndex,
            cardId: card.card_id || null
          }));

          const enrichedKept = batchKept.map(item => ({
            ...item,
            id: `kept_${originalRowIndex}_${item.language}`,
            cardIndex: originalRowIndex,
            cardId: card.card_id || null
          }));

          allSuggestions.push(...enrichedSuggestions);
          allKept.push(...enrichedKept);
          
          // Update state progressively after each language batch
          setSuggestions([...allSuggestions]);
          setKeptTranslations([...allKept]);

          // Small delay between language batches
          if (langBatchStart + LANGUAGE_BATCH_SIZE < cardLanguages.length) {
            await new Promise(resolve => setTimeout(resolve, 300));
          }
        } catch (error) {
          console.error(`Error proofreading card ${i}, language batch starting at ${langBatchStart}:`, error);
        }
      }

      // Update card progress after all language batches for this card are done
      setProcessedCards(i + 1);
      setCurrentCardLanguages({ current: 0, total: 0 }); // Reset for next card

      // Small delay between cards
      if (i < cardsToProcess.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 500));
      }
    }

    setProcessing(false);
    setCurrentCardLanguages({ current: 0, total: 0 }); // Reset when done
    setTotalCards(cardsToProcess.length); // Update total cards to reflect selected count
  };

  const handleAccept = (suggestionId) => {
    setAcceptedSuggestions(prev => new Set([...prev, suggestionId]));
    setRejectedSuggestions(prev => {
      const newSet = new Set(prev);
      newSet.delete(suggestionId);
      return newSet;
    });
  };

  const handleReject = (suggestionId) => {
    setRejectedSuggestions(prev => new Set([...prev, suggestionId]));
    setAcceptedSuggestions(prev => {
      const newSet = new Set(prev);
      newSet.delete(suggestionId);
      return newSet;
    });
  };

  const handleAcceptAll = () => {
    // Accept all pending suggestions (not accepted or rejected) that have meaningful changes
    const allPendingIds = suggestions
      .filter(s => {
        if (acceptedSuggestions.has(s.id) || rejectedSuggestions.has(s.id)) {
          return false;
        }
        // Only include suggestions with meaningful changes
        return hasMeaningfulChange(s.original, s.suggested);
      })
      .map(s => s.id);
    setAcceptedSuggestions(prev => new Set([...prev, ...allPendingIds]));
    setRejectedSuggestions(prev => {
      const newSet = new Set(prev);
      allPendingIds.forEach(id => newSet.delete(id));
      return newSet;
    });
  };

  const handleAskGoogle = async (suggestion) => {
    const suggestionId = suggestion.id;
    const language = suggestion.language;
    
    // Set loading state
    setGoogleTranslateLoading(prev => new Set([...prev, suggestionId]));
    
    try {
      // Determine reference language - use English if available, otherwise first language in columns
      const referenceLanguage = columns.includes('en') ? 'en' : (columns.find(col => col !== null) || 'en');
      
      // Skip if the language is already the reference language
      if (language === referenceLanguage) {
        setGoogleTranslateResults(prev => ({
          ...prev,
          [suggestionId]: {
            error: 'Cannot compare translations when the language is already the reference language.',
            referenceLanguage
          }
        }));
        setGoogleTranslateLoading(prev => {
          const newSet = new Set(prev);
          newSet.delete(suggestionId);
          return newSet;
        });
        return;
      }

      // Translate both original and suggested text to reference language
      const [originalResult, suggestedResult] = await Promise.all([
        translateText(suggestion.original.trim(), referenceLanguage),
        translateText(suggestion.suggested.trim(), referenceLanguage)
      ]);

      if (originalResult.success && suggestedResult.success) {
        const originalTranslated = Array.isArray(originalResult.data) ? originalResult.data[0] : originalResult.data;
        const suggestedTranslated = Array.isArray(suggestedResult.data) ? suggestedResult.data[0] : suggestedResult.data;
        
        // Determine which is better (simpler comparison - if they're the same, both are good)
        // In a real scenario, you might want more sophisticated comparison
        const areSame = originalTranslated.toLowerCase().trim() === suggestedTranslated.toLowerCase().trim();
        
        setGoogleTranslateResults(prev => ({
          ...prev,
          [suggestionId]: {
            originalTranslated,
            suggestedTranslated,
            referenceLanguage,
            recommendation: areSame ? 'both' : 'suggested', // If different, suggest the AI's suggestion
            areSame
          }
        }));
      } else {
        setGoogleTranslateResults(prev => ({
          ...prev,
          [suggestionId]: {
            error: originalResult.error || suggestedResult.error || 'Translation failed',
            referenceLanguage
          }
        }));
      }
    } catch (error) {
      console.error('Error in handleAskGoogle:', error);
      setGoogleTranslateResults(prev => ({
        ...prev,
        [suggestionId]: {
          error: error.message || 'An error occurred while translating',
          referenceLanguage: columns.includes('en') ? 'en' : (columns.find(col => col !== null) || 'en')
        }
      }));
    } finally {
      setGoogleTranslateLoading(prev => {
        const newSet = new Set(prev);
        newSet.delete(suggestionId);
        return newSet;
      });
    }
  };

  const handleApplyChanges = () => {
    const accepted = suggestions.filter(s => acceptedSuggestions.has(s.id));
    onApplyChanges(accepted);
    closeFunction();
  };

  // Helper function to detect update type from explanation
  const getUpdateType = (explanation) => {
    const lowerExplanation = explanation.toLowerCase();
    if (lowerExplanation.includes('spell') || lowerExplanation.includes('typo')) {
      return 'spelling';
    }
    if (lowerExplanation.includes('grammar') || lowerExplanation.includes('grammatical')) {
      return 'grammar';
    }
    if (lowerExplanation.includes('word choice') || lowerExplanation.includes('better word') || lowerExplanation.includes('more appropriate')) {
      return 'word-choice';
    }
    if (lowerExplanation.includes('capital') || lowerExplanation.includes('punctuation') || lowerExplanation.includes('format') || lowerExplanation.includes('spacing')) {
      return 'formatting';
    }
    return 'other';
  };

  // Filter suggestions based on severity, status, and update type
  const filteredSuggestions = suggestions.filter(suggestion => {
    // Only show suggestions with meaningful changes
    if (!hasMeaningfulChange(suggestion.original, suggestion.suggested)) {
      return false;
    }
    
    // Severity filter
    if (severityFilter !== 'all' && suggestion.severity !== severityFilter) {
      return false;
    }
    
    // Status filter
    const isAccepted = acceptedSuggestions.has(suggestion.id);
    const isRejected = rejectedSuggestions.has(suggestion.id);
    const status = isAccepted ? 'accepted' : isRejected ? 'rejected' : 'pending';
    
    if (statusFilter !== 'all' && status !== statusFilter) {
      return false;
    }
    
    // Update type filter
    if (updateTypeFilter !== 'all') {
      const updateType = getUpdateType(suggestion.explanation);
      if (updateType !== updateTypeFilter) {
        return false;
      }
    }
    
    return true;
  });

  // Sort filtered suggestions
  const sortedSuggestions = [...filteredSuggestions].sort((a, b) => {
    if (sortBy === 'severity') {
      const severityOrder = { error: 0, warning: 1, suggestion: 2 };
      return (severityOrder[a.severity] || 3) - (severityOrder[b.severity] || 3);
    }
    if (sortBy === 'language') {
      return a.language.localeCompare(b.language);
    }
    if (sortBy === 'card') {
      return a.cardIndex - b.cardIndex;
    }
    return 0; // 'none' - no sorting
  });

  // Group sorted suggestions by card (already filtered to only meaningful changes)
  const suggestionsByCard = {};
  sortedSuggestions.forEach(suggestion => {
    const cardIndex = suggestion.cardIndex;
    if (!suggestionsByCard[cardIndex]) {
      suggestionsByCard[cardIndex] = [];
    }
    suggestionsByCard[cardIndex].push(suggestion);
  });

  // Group kept translations by card
  const keptByCard = {};
  keptTranslations.forEach(item => {
    const cardIndex = item.cardIndex;
    if (!keptByCard[cardIndex]) {
      keptByCard[cardIndex] = [];
    }
    keptByCard[cardIndex].push(item);
  });

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'error':
        return 'text-red-600 bg-red-50 border-red-200';
      case 'warning':
        return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'suggestion':
        return 'text-blue-600 bg-blue-50 border-blue-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'error':
        return faExclamationTriangle;
      case 'warning':
        return faExclamationTriangle;
      case 'suggestion':
        return faInfoCircle;
      default:
        return faInfoCircle;
    }
  };

  const getCardIdentifier = (cardIndex) => {
    const card = cards[cardIndex];
    if (!card) return `Card ${cardIndex + 1}`;
    
    // Try to find first non-empty translation
    const validColumns = columns.filter(col => col !== null);
    for (const lang of validColumns) {
      if (card[lang] && card[lang] !== null && card[lang] !== "") {
        return `${card[lang].substring(0, 30)}${card[lang].length > 30 ? '...' : ''}`;
      }
    }
    return `Card ${cardIndex + 1}`;
  };

  return (
    <Modal 
      content={<ModalContent 
        processing={processing}
        processedCards={processedCards}
        totalCards={totalCards}
        currentCardLanguages={currentCardLanguages}
        suggestions={suggestions}
        suggestionsByCard={suggestionsByCard}
        keptByCard={keptByCard}
        cards={cards}
        columns={columns}
        acceptedSuggestions={acceptedSuggestions}
        rejectedSuggestions={rejectedSuggestions}
        severityFilter={severityFilter}
        statusFilter={statusFilter}
        updateTypeFilter={updateTypeFilter}
        sortBy={sortBy}
        showKept={showKept}
        setSeverityFilter={setSeverityFilter}
        setStatusFilter={setStatusFilter}
        setUpdateTypeFilter={setUpdateTypeFilter}
        setSortBy={setSortBy}
        setShowKept={setShowKept}
        handleAccept={handleAccept}
        handleReject={handleReject}
        handleAcceptAll={handleAcceptAll}
        handleApplyChanges={handleApplyChanges}
        startProofreading={startProofreading}
        expandedSuggestions={expandedSuggestions}
        setExpandedSuggestions={setExpandedSuggestions}
        hasMeaningfulChange={hasMeaningfulChange}
        getSeverityColor={getSeverityColor}
        getSeverityIcon={getSeverityIcon}
        getCardIdentifier={getCardIdentifier}
        deckContext={deckContext}
        setDeckContext={setDeckContext}
        selectedCards={selectedCards}
        setSelectedCards={setSelectedCards}
        selectAllCards={selectAllCards}
        setSelectAllCards={setSelectAllCards}
        handleSelectAllCards={handleSelectAllCards}
        handleToggleCard={handleToggleCard}
        handleSuggestPrompt={handleSuggestPrompt}
        suggestingPrompt={suggestingPrompt}
        googleTranslateLoading={googleTranslateLoading}
        googleTranslateResults={googleTranslateResults}
        handleAskGoogle={handleAskGoogle}
      />} 
      closeFunction={closeFunction} 
      size="3xl" 
    />
  );
};

const ModalContent = ({
  processing,
  processedCards,
  totalCards,
  currentCardLanguages,
  suggestions,
  suggestionsByCard,
  keptByCard,
  cards,
  columns,
  acceptedSuggestions,
  rejectedSuggestions,
  severityFilter,
  statusFilter,
  updateTypeFilter,
  sortBy,
  showKept,
  setSeverityFilter,
  setStatusFilter,
  setUpdateTypeFilter,
  setSortBy,
  setShowKept,
  handleAccept,
  handleReject,
  handleAcceptAll,
  handleApplyChanges,
  startProofreading,
  expandedSuggestions,
  setExpandedSuggestions,
  hasMeaningfulChange,
  getSeverityColor,
  getSeverityIcon,
  getCardIdentifier,
  deckContext,
  setDeckContext,
  selectedCards,
  setSelectedCards,
  selectAllCards,
  setSelectAllCards,
  handleSelectAllCards,
  handleToggleCard,
  handleSuggestPrompt,
  suggestingPrompt,
  googleTranslateLoading,
  googleTranslateResults,
  handleAskGoogle
}) => {
  const totalSuggestions = Object.values(suggestionsByCard).flat().length;
  const totalKept = Object.values(keptByCard).flat().length;
  const acceptedCount = acceptedSuggestions.size;
  const rejectedCount = rejectedSuggestions.size;
  
  // Count only meaningful suggestions
  const meaningfulSuggestions = suggestions.filter(s => hasMeaningfulChange(s.original, s.suggested));
  const pendingCount = meaningfulSuggestions.filter(s => !acceptedSuggestions.has(s.id) && !rejectedSuggestions.has(s.id)).length;
  const hasStarted = processedCards > 0 || totalSuggestions > 0 || totalKept > 0;
  
  // Count suggestions by severity (only meaningful ones)
  const errorCount = meaningfulSuggestions.filter(s => s.severity === 'error' && !rejectedSuggestions.has(s.id)).length;
  const warningCount = meaningfulSuggestions.filter(s => s.severity === 'warning' && !rejectedSuggestions.has(s.id)).length;
  const suggestionCount = meaningfulSuggestions.filter(s => s.severity === 'suggestion' && !rejectedSuggestions.has(s.id)).length;
  
  // Count by update type
  const getUpdateType = (explanation) => {
    const lowerExplanation = explanation.toLowerCase();
    if (lowerExplanation.includes('spell') || lowerExplanation.includes('typo')) return 'spelling';
    if (lowerExplanation.includes('grammar') || lowerExplanation.includes('grammatical')) return 'grammar';
    if (lowerExplanation.includes('word choice') || lowerExplanation.includes('better word') || lowerExplanation.includes('more appropriate')) return 'word-choice';
    if (lowerExplanation.includes('capital') || lowerExplanation.includes('punctuation') || lowerExplanation.includes('format') || lowerExplanation.includes('spacing')) return 'formatting';
    return 'other';
  };
  
  const spellingCount = meaningfulSuggestions.filter(s => getUpdateType(s.explanation) === 'spelling' && !rejectedSuggestions.has(s.id)).length;
  const grammarCount = meaningfulSuggestions.filter(s => getUpdateType(s.explanation) === 'grammar' && !rejectedSuggestions.has(s.id)).length;
  const wordChoiceCount = meaningfulSuggestions.filter(s => getUpdateType(s.explanation) === 'word-choice' && !rejectedSuggestions.has(s.id)).length;
  const formattingCount = meaningfulSuggestions.filter(s => getUpdateType(s.explanation) === 'formatting' && !rejectedSuggestions.has(s.id)).length;
  const otherCount = meaningfulSuggestions.filter(s => getUpdateType(s.explanation) === 'other' && !rejectedSuggestions.has(s.id)).length;

  return (
    <div className="w-full">
      <h2 className="text-2xl font-bold mb-4 dark:text-white">AI Proofreader</h2>
      
      {/* Start button */}
      {!hasStarted && !processing && (
        <div className="mb-6 space-y-4">
          <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div className="mb-4">
              <label htmlFor="deck-context" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Deck Context (Optional)
              </label>
              <textarea
                id="deck-context"
                value={deckContext}
                onChange={(e) => setDeckContext(e.target.value)}
                placeholder="Provide context about the deck's purpose, target audience, or specific word meanings. For example: 'This deck is for learning colors', 'These are technical terms for software development', 'Words should be formal/professional', etc."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white resize-y min-h-[100px]"
                rows="4"
              />
              <div className="flex items-center justify-between mt-1">
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  This context helps the AI understand the purpose and appropriate word choices for your deck.
                </p>
                <button
                  onClick={handleSuggestPrompt}
                  disabled={suggestingPrompt || cards.length === 0}
                  className="text-xs px-2 py-1 text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 disabled:opacity-50 disabled:cursor-not-allowed transition"
                  title="Use AI to suggest a context prompt based on your deck content"
                >
                  {suggestingPrompt ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-1" />
                      Suggesting...
                    </>
                  ) : (
                    "Suggest prompt"
                  )}
                </button>
              </div>
            </div>

            {/* Card Selection */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-3">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Select Cards to Proofread
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="selectAllCards"
                    checked={selectAllCards}
                    onChange={(e) => handleSelectAllCards(e.target.checked)}
                    className="rounded"
                  />
                  <label htmlFor="selectAllCards" className="text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                    Select All ({cards.length})
                  </label>
                </div>
              </div>
              <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-lg p-3 bg-white dark:bg-gray-800">
                {cards.length === 0 ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400">No cards available</p>
                ) : (
                  <div className="space-y-2">
                    {cards.map((card, index) => {
                      const validColumns = columns.filter(col => col !== null);
                      const cardPreview = validColumns
                        .map(lang => card[lang])
                        .filter(val => val && val !== null && val !== "")
                        .slice(0, 2)
                        .join(" / ") || `Card ${index + 1}`;
                      
                      return (
                        <div key={index} className="flex items-center gap-2 p-2 hover:bg-gray-50 dark:hover:bg-gray-700 rounded">
                          <input
                            type="checkbox"
                            id={`card-${index}`}
                            checked={selectedCards.has(index)}
                            onChange={() => handleToggleCard(index)}
                            className="rounded"
                          />
                          <label 
                            htmlFor={`card-${index}`} 
                            className="text-sm text-gray-700 dark:text-gray-300 cursor-pointer flex-1"
                          >
                            {cardPreview.length > 60 ? `${cardPreview.substring(0, 60)}...` : cardPreview}
                          </label>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                {selectedCards.size} of {cards.length} card{cards.length !== 1 ? 's' : ''} selected
              </p>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-800 font-medium mb-2">
                  Ready to proofread {selectedCards.size} card{selectedCards.size !== 1 ? 's' : ''}
                </p>
                <p className="text-blue-600 text-sm">
                  This will check all translations for accuracy, grammar, spelling, and consistency.
                </p>
              </div>
              <button
                onClick={startProofreading}
                disabled={selectedCards.size === 0}
                className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                title={selectedCards.size === 0 ? "Please select at least one card" : ""}
              >
                Start Process
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Progress indicator */}
      {processing && (
        <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-3">
              <FontAwesomeIcon icon={faSpinner} className="animate-spin text-blue-600" />
              <span className="text-blue-800 font-medium">
                Processing card {processedCards} of {totalCards}...
              </span>
            </div>
            {currentCardLanguages.total > 0 && (
              <div className="text-blue-700 text-sm ml-7">
                Checking languages {currentCardLanguages.current} of {currentCardLanguages.total} for current card...
              </div>
            )}
          </div>
        </div>
      )}

      {/* Summary and Filters */}
      {hasStarted && !processing && (
        <div className="mb-6 space-y-4">
          <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <div>
                <span className="text-gray-700 font-medium">
                  Found {totalSuggestions} suggestion{totalSuggestions !== 1 ? 's' : ''} across {Object.keys(suggestionsByCard).length} card{Object.keys(suggestionsByCard).length !== 1 ? 's' : ''}
                </span>
                {totalKept > 0 && (
                  <span className="text-gray-600 text-sm ml-3">
                    • {totalKept} translation{totalKept !== 1 ? 's' : ''} verified correct
                  </span>
                )}
              </div>
              <div>
                <span className="text-green-600 font-medium">
                  {acceptedCount} accepted
                </span>
              </div>
            </div>
            
            {/* Filters */}
            <div className="space-y-3">
              <div className="flex flex-wrap gap-4 items-center">
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium text-gray-700">Severity:</label>
                  <select
                    value={severityFilter}
                    onChange={(e) => setSeverityFilter(e.target.value)}
                    className="px-3 py-1 border border-gray-300 rounded text-sm"
                  >
                    <option value="all">All ({totalSuggestions})</option>
                    <option value="error">Errors ({errorCount})</option>
                    <option value="warning">Warnings ({warningCount})</option>
                    <option value="suggestion">Suggestions ({suggestionCount})</option>
                  </select>
                </div>
                
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium text-gray-700">Status:</label>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="px-3 py-1 border border-gray-300 rounded text-sm"
                  >
                    <option value="all">All ({totalSuggestions})</option>
                    <option value="pending">Pending ({pendingCount})</option>
                    <option value="accepted">Accepted ({acceptedCount})</option>
                    <option value="rejected">Rejected ({rejectedCount})</option>
                  </select>
                </div>
                
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium text-gray-700">Update Type:</label>
                  <select
                    value={updateTypeFilter}
                    onChange={(e) => setUpdateTypeFilter(e.target.value)}
                    className="px-3 py-1 border border-gray-300 rounded text-sm"
                  >
                    <option value="all">All ({totalSuggestions})</option>
                    <option value="spelling">Spelling ({spellingCount})</option>
                    <option value="grammar">Grammar ({grammarCount})</option>
                    <option value="word-choice">Word Choice ({wordChoiceCount})</option>
                    <option value="formatting">Formatting ({formattingCount})</option>
                    <option value="other">Other ({otherCount})</option>
                  </select>
                </div>
                
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium text-gray-700">Sort By:</label>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="px-3 py-1 border border-gray-300 rounded text-sm"
                  >
                    <option value="none">None</option>
                    <option value="severity">Severity</option>
                    <option value="language">Language</option>
                    <option value="card">Card</option>
                  </select>
                </div>
                
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="showKept"
                    checked={showKept}
                    onChange={(e) => setShowKept(e.target.checked)}
                    className="rounded"
                  />
                  <label htmlFor="showKept" className="text-sm font-medium text-gray-700 cursor-pointer">
                    Show verified correct ({totalKept})
                  </label>
                </div>
              </div>
              
              {/* Accept All button */}
              {pendingCount > 0 && (
                <div className="flex justify-end">
                  <button
                    onClick={handleAcceptAll}
                    className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition text-sm font-medium"
                  >
                    Accept All Pending ({pendingCount})
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Suggestions list */}
      <div className="max-h-96 overflow-y-auto space-y-6">
        {hasStarted && totalSuggestions === 0 && !processing && (
          <div className="text-center py-8 text-gray-500">
            <p className="text-lg">No issues found! All translations look good.</p>
          </div>
        )}
        
        {hasStarted && suggestions.length > 0 && Object.keys(suggestionsByCard).length === 0 && (severityFilter !== 'all' || statusFilter !== 'all' || updateTypeFilter !== 'all') && (
          <div className="text-center py-8 text-gray-500">
            <p className="text-lg">No suggestions match the current filters. Try adjusting your filters.</p>
          </div>
        )}
        
        {hasStarted && suggestions.length > 0 && Object.keys(suggestionsByCard).length === 0 && severityFilter === 'all' && statusFilter === 'all' && updateTypeFilter === 'all' && (
          <div className="text-center py-8 text-gray-500">
            <p className="text-lg">All suggestions only have whitespace differences. No meaningful changes found.</p>
          </div>
        )}

        {Object.keys(suggestionsByCard).map(cardIndex => {
          const cardSuggestions = suggestionsByCard[cardIndex];
          const cardId = getCardIdentifier(parseInt(cardIndex));

          return (
            <div key={cardIndex} className="border border-gray-200 rounded-lg p-4 bg-white">
              <h3 className="font-semibold text-lg mb-3 text-gray-800 dark:text-white">
                {cardId}
              </h3>
              
              <div className="space-y-3">
                {cardSuggestions.map(suggestion => {
                  const isAccepted = acceptedSuggestions.has(suggestion.id);
                  const isRejected = rejectedSuggestions.has(suggestion.id);
                  const borderColor = isAccepted 
                    ? 'border-green-400 bg-green-50' 
                    : isRejected 
                    ? 'border-gray-300 bg-gray-50 opacity-60' 
                    : 'border-gray-200';

                  const isExpanded = expandedSuggestions.has(suggestion.id);
                  
                  return (
                    <div 
                      key={suggestion.id} 
                      className={`border rounded-lg ${borderColor} transition-all`}
                    >
                      {/* Header - always visible */}
                      <div 
                        className="p-4 cursor-pointer hover:bg-opacity-80 transition"
                        onClick={() => {
                          const newExpanded = new Set(expandedSuggestions);
                          if (isExpanded) {
                            newExpanded.delete(suggestion.id);
                          } else {
                            newExpanded.add(suggestion.id);
                          }
                          setExpandedSuggestions(newExpanded);
                        }}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-2 flex-1">
                            <FontAwesomeIcon 
                              icon={isExpanded ? faChevronUp : faChevronDown} 
                              className="text-gray-400 text-xs mr-1"
                            />
                            <span className={`px-2 py-1 rounded text-xs font-medium ${getSeverityColor(suggestion.severity)}`}>
                              <FontAwesomeIcon icon={getSeverityIcon(suggestion.severity)} className="mr-1" />
                              {suggestion.severity.toUpperCase()}
                            </span>
                            <span className="text-sm font-medium text-gray-600 dark:text-gray-300">
                              {suggestion.language.toUpperCase()}
                            </span>
                            <span className="text-sm text-gray-500 dark:text-gray-400 ml-2">
                              {suggestion.original.substring(0, 40)}{suggestion.original.length > 40 ? '...' : ''}
                            </span>
                            <span className="text-sm text-blue-600 dark:text-blue-400 ml-2">
                              → {suggestion.suggested.substring(0, 40)}{suggestion.suggested.length > 40 ? '...' : ''}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            {isAccepted && (
                              <span className="text-green-600 text-sm font-medium">✓ Accepted</span>
                            )}
                            {isRejected && (
                              <span className="text-gray-400 text-sm">Rejected</span>
                            )}
                          </div>
                        </div>
                        {/* Always show explanation */}
                        {suggestion.explanation && (
                          <div className="mt-3 pt-3 border-t border-gray-200">
                            <div className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Reason:</div>
                            <div className="text-sm text-gray-700 dark:text-gray-300 italic leading-relaxed">
                              {suggestion.explanation}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Expandable content for full details */}
                      {isExpanded && (
                        <div className="px-4 pb-4 border-t border-gray-200 pt-3 space-y-3">
                          <div>
                            <div className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Original (full):</div>
                            <div className="text-base text-gray-800 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 p-3 rounded">
                              {suggestion.original}
                            </div>
                          </div>

                          <div>
                            <div className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Suggested (full):</div>
                            <div className="text-base font-medium text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-900 p-3 rounded">
                              {suggestion.suggested}
                            </div>
                          </div>

                          {/* Google Translate Results */}
                          {googleTranslateResults[suggestion.id] && (
                            <div className="mt-4 p-4 bg-yellow-50 dark:bg-yellow-900 rounded-lg border border-yellow-200 dark:border-yellow-700">
                              <div className="text-sm font-semibold text-yellow-800 dark:text-yellow-200 mb-3">
                                <FontAwesomeIcon icon={faSearch} className="mr-2" />
                                Google Translate Comparison (to {googleTranslateResults[suggestion.id].referenceLanguage?.toUpperCase() || 'EN'})
                              </div>
                              {googleTranslateResults[suggestion.id].error ? (
                                <div className="text-sm text-red-600 dark:text-red-400">
                                  {googleTranslateResults[suggestion.id].error}
                                </div>
                              ) : (
                                <div className="space-y-3">
                                  <div>
                                    <div className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Original translated:</div>
                                    <div className="text-sm text-gray-800 dark:text-gray-200 bg-white dark:bg-gray-800 p-2 rounded">
                                      {googleTranslateResults[suggestion.id].originalTranslated}
                                    </div>
                                  </div>
                                  <div>
                                    <div className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Suggested translated:</div>
                                    <div className="text-sm text-gray-800 dark:text-gray-200 bg-white dark:bg-gray-800 p-2 rounded">
                                      {googleTranslateResults[suggestion.id].suggestedTranslated}
                                    </div>
                                  </div>
                                  <div className="pt-2 border-t border-yellow-200 dark:border-yellow-700">
                                    {googleTranslateResults[suggestion.id].areSame ? (
                                      <div className="text-sm text-green-700 dark:text-green-400 font-medium">
                                        ✓ Both translations produce the same result in {googleTranslateResults[suggestion.id].referenceLanguage?.toUpperCase()}. Both are valid.
                                      </div>
                                    ) : (
                                      <div className="text-sm text-blue-700 dark:text-blue-400 font-medium">
                                        → The suggested translation produces a different result. Review both options carefully.
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Action buttons */}
                      <div className={`px-4 pb-4 flex gap-2 items-center flex-wrap ${isExpanded ? '' : 'pt-2'}`}>
                        {!hasMeaningfulChange(suggestion.original, suggestion.suggested) && (
                          <span className="text-xs text-gray-500 italic">
                            No meaningful change (only whitespace differences)
                          </span>
                        )}
                        {!isAccepted && !isRejected && hasMeaningfulChange(suggestion.original, suggestion.suggested) && (
                          <>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleAccept(suggestion.id);
                              }}
                              className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition text-sm font-medium"
                            >
                              <FontAwesomeIcon icon={faCheck} className="mr-1" />
                              Accept
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleReject(suggestion.id);
                              }}
                              className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition text-sm font-medium"
                            >
                              <FontAwesomeIcon icon={faX} className="mr-1" />
                              Reject
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                if (!isExpanded) {
                                  // Expand first if not expanded
                                  const newExpanded = new Set(expandedSuggestions);
                                  newExpanded.add(suggestion.id);
                                  setExpandedSuggestions(newExpanded);
                                }
                                handleAskGoogle(suggestion);
                              }}
                              disabled={googleTranslateLoading.has(suggestion.id)}
                              className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {googleTranslateLoading.has(suggestion.id) ? (
                                <>
                                  <FontAwesomeIcon icon={faSpinner} className="mr-1 animate-spin" />
                                  Checking...
                                </>
                              ) : (
                                <>
                                  <FontAwesomeIcon icon={faSearch} className="mr-1" />
                                  Ask Google
                                </>
                              )}
                            </button>
                          </>
                        )}
                        {!isAccepted && !isRejected && !hasMeaningfulChange(suggestion.original, suggestion.suggested) && (
                          <span className="text-xs text-gray-400">Actions disabled - no meaningful change</span>
                        )}

                        {(isAccepted || isRejected) && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (isAccepted) {
                                handleReject(suggestion.id);
                              } else {
                                handleAccept(suggestion.id);
                              }
                            }}
                            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition text-sm"
                          >
                            {isAccepted ? 'Undo Accept' : 'Undo Reject'}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
        
        {/* Kept translations (verified correct) */}
        {showKept && hasStarted && Object.keys(keptByCard).length > 0 && (
          <div className="mt-8 pt-6 border-t border-gray-300">
            <h3 className="text-lg font-semibold mb-4 text-gray-700 dark:text-gray-300">
              Verified Correct Translations ({totalKept})
            </h3>
            {Object.keys(keptByCard).map(cardIndex => {
              const keptItems = keptByCard[cardIndex];
              const cardId = getCardIdentifier(parseInt(cardIndex));

              return (
                <div key={`kept_${cardIndex}`} className="mb-6 border border-green-200 rounded-lg p-4 bg-green-50">
                  <h4 className="font-semibold text-base mb-3 text-gray-800 dark:text-white">
                    {cardId}
                  </h4>
                  
                  <div className="space-y-2">
                    {keptItems.map(item => (
                      <div 
                        key={item.id} 
                        className="border border-green-300 rounded-lg p-3 bg-white"
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <span className="px-2 py-1 rounded text-xs font-medium text-green-700 bg-green-100">
                            {item.language.toUpperCase()}
                          </span>
                          <span className="text-green-600 text-xs font-medium">✓ Verified Correct</span>
                        </div>

                        <div className="mb-2">
                          <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Translation:</div>
                          <div className="text-base text-gray-800 dark:text-gray-200 bg-gray-50 dark:bg-gray-700 p-2 rounded">
                            {item.original}
                          </div>
                        </div>

                        <div>
                          <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Reason:</div>
                          <div className="text-sm text-gray-700 dark:text-gray-300 italic">
                            {item.explanation}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Action buttons */}
      {hasStarted && !processing && suggestions.length > 0 && (
        <div className="mt-6 flex justify-end gap-3">
          <button
            onClick={handleApplyChanges}
            disabled={acceptedCount === 0}
            className={`px-6 py-2 rounded-lg font-medium transition ${
              acceptedCount > 0
                ? 'bg-green-500 text-white hover:bg-green-600'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            Apply All Accepted Changes ({acceptedCount})
          </button>
        </div>
      )}
    </div>
  );
};

export default ProofreaderModal;
